import React from 'react';
import { getNestedValue, getColorClasses } from './utils';

/**
 * DataTableWidget - Renders an inline data table within the panel
 * 
 * Config (from config_json):
 * {
 *   "type": "data_table",
 *   "options": {
 *     "dataKey": "riskData",           // Key in enrichedData containing the array
 *     "maxRows": 5,                     // Max visible rows (0 = show all)
 *     "emptyMessage": "데이터 없음",     // Message when no data
 *     "columns": [
 *       { "header": "코드", "key": "risk_factor_code", "width": 80, "align": "center" },
 *       { "header": "위험 레벨", "key": "risk_danger_level", "width": 60, "align": "center",
 *         "badge": true, "badgeColors": { "3": "red", "4": "red", "5": "red", "2": "orange", "1": "emerald" }
 *       },
 *       { "header": "위험 상황", "key": "risk_situation" }
 *     ],
 *     "highlightColor": "indigo"        // Row hover color
 *   },
 *   "items": []
 * }
 */

/**
 * Badge component for cell values
 */
const CellBadge = ({ value, colorName }) => {
  const colors = getColorClasses(colorName || 'slate');
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-black ${colors.bgLight} ${colors.text}`}
      style={{ border: `1px solid ${colors.border}` }}
    >
      {value}
    </span>
  );
};

export default function DataTableWidget({ config, data }) {
  if (!config) return null;

  const {
    dataKey,
    columns = [],
    maxRows = 5,
    emptyMessage = '데이터가 없습니다',
  } = config;

  // Get table data from enrichedData
  const tableData = getNestedValue(data, dataKey, []);
  const rows = Array.isArray(tableData) ? tableData : [];
  const displayRows = maxRows > 0 ? rows.slice(0, maxRows) : rows;

  if (columns.length === 0) return null;

  return (
    <div>
      {config.title && (
        <div className="flex items-center justify-between px-1 mb-2">
          <span className="text-[12px] text-slate-400 font-bold">{config.title}</span>
          {rows.length > maxRows && maxRows > 0 && (
            <span className="text-[10px] text-slate-600 font-bold">
              {maxRows}/{rows.length}건
            </span>
          )}
        </div>
      )}

      {displayRows.length === 0 ? (
        <div
          className="flex items-center justify-center py-6 rounded-2xl bg-white/5 text-slate-600 text-[11px] font-bold"
          style={{ border: '1px solid rgba(255,255,255,0.04)' }}
        >
          {emptyMessage}
        </div>
      ) : (
        <div
          className="rounded-xl overflow-hidden"
          style={{ border: '1px solid rgba(255,255,255,0.06)' }}
        >
          {/* Header */}
          <div
            className="flex bg-white/5"
            style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
          >
            {columns.map((col, idx) => (
              <div
                key={idx}
                className="px-2.5 py-2 text-[10px] font-black text-slate-500 uppercase tracking-wider"
                style={{
                  width: col.width ? `${col.width}px` : undefined,
                  flex: col.width ? `0 0 ${col.width}px` : 1,
                  textAlign: col.align || 'left',
                }}
              >
                {col.header}
              </div>
            ))}
          </div>

          {/* Body */}
          <div className="max-h-[200px] overflow-y-auto [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-white/10 [&::-webkit-scrollbar-thumb]:rounded-full">
            {displayRows.map((row, rowIdx) => (
              <div
                key={rowIdx}
                className="flex hover:bg-white/5 transition-colors"
                style={{
                  borderBottom: rowIdx < displayRows.length - 1 ? '1px solid rgba(255,255,255,0.03)' : 'none',
                }}
              >
                {columns.map((col, colIdx) => {
                  const cellValue = row[col.key];
                  const displayValue = cellValue ?? '-';

                  return (
                    <div
                      key={colIdx}
                      className="px-2.5 py-2 text-[11px] text-slate-300 font-medium truncate"
                      style={{
                        width: col.width ? `${col.width}px` : undefined,
                        flex: col.width ? `0 0 ${col.width}px` : 1,
                        textAlign: col.align || 'left',
                      }}
                    >
                      {col.badge && col.badgeColors ? (
                        <CellBadge
                          value={displayValue}
                          colorName={col.badgeColors[String(cellValue)] || 'slate'}
                        />
                      ) : (
                        displayValue
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Footer - show total count if truncated */}
          {rows.length > maxRows && maxRows > 0 && (
            <div
              className="px-2.5 py-1.5 text-center text-[10px] text-slate-600 font-bold bg-white/3"
              style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}
            >
              외 {rows.length - maxRows}건 더보기
            </div>
          )}
        </div>
      )}
    </div>
  );
}
